import { useEffect, useRef, useState } from "react";

/**
 * DemoShowcase — "watch it work" section for the landing page.
 *
 * One large browser-chrome frame with a mono-caps tab rail above it; each
 * tab is a short product capture of one core task (score, warp, track,
 * prep). Videos are muted loops that lazy-load and only play while the
 * frame is actually on screen. Real captures land later (see
 * public/videos/README.md); until a file exists the slot quietly shows
 * its poster instead, so the section ships before the recordings do.
 */

type Demo = {
  id: string;
  tab: string;
  title: string;
  caption: string;
  /** Fake address-bar text shown in the browser chrome. */
  url: string;
  video: string;
  poster: string;
};

const DEMOS: Demo[] = [
  {
    id: "score",
    tab: "Score the fit",
    title: "Score any posting in one click",
    caption:
      "JobWarp reads the JD on the page, checks it against your CV, and shows the score with its reasoning. Scoring is always free.",
    url: "linkedin.com/jobs/view/4012387",
    video: "/videos/demo-score.mp4",
    poster: "/videos/poster-score.webp",
  },
  {
    id: "warp",
    tab: "Warp it",
    title: "Draft the full application in seconds",
    caption:
      "One Warp drafts the cover letter and screening answers from your real experience, grounded in what it found about the company.",
    url: "jobwarp.app/app/jobs/vercel-em",
    video: "/videos/demo-warp.mp4",
    poster: "/videos/poster-warp.webp",
  },
  {
    id: "track",
    tab: "Track the funnel",
    title: "A kanban that keeps itself current",
    caption:
      "JobWarp logs the application when you send it and keeps every column accurate as replies come in. No spreadsheet on the side.",
    url: "jobwarp.app/app",
    video: "/videos/demo-track.mp4",
    poster: "/videos/poster-track.webp",
  },
  {
    id: "prep",
    tab: "Prep the interview",
    title: "Walk in already prepared",
    caption:
      "When the interview lands, JobWarp generates likely questions for this role and company, with a draft answer for each based on your CV.",
    url: "jobwarp.app/app/jobs/linear-be",
    video: "/videos/demo-prep.mp4",
    poster: "/videos/poster-prep.webp",
  },
];

function usePrefersReducedMotion() {
  const [reduce, setReduce] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const sync = () => setReduce(mq.matches);
    sync();
    mq.addEventListener("change", sync);
    return () => mq.removeEventListener("change", sync);
  }, []);
  return reduce;
}

/**
 * The video slot. Plays only while >40% visible, never under reduced
 * motion, and falls back to the poster image if the capture file is
 * missing or fails to decode.
 */
function DemoVideo({ demo, active }: { demo: Demo; active: boolean }) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [inView, setInView] = useState(false);
  const [broken, setBroken] = useState(false);
  const reduce = usePrefersReducedMotion();

  useEffect(() => {
    const el = videoRef.current;
    if (!el) return;
    const io = new IntersectionObserver(
      ([entry]) => setInView(entry?.isIntersecting ?? false),
      { threshold: 0.4 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  useEffect(() => {
    const el = videoRef.current;
    if (!el || broken) return;
    if (active && inView && !reduce) {
      el.play().catch(() => setBroken(true));
    } else {
      el.pause();
    }
  }, [active, inView, reduce, broken]);

  return (
    <div
      className="relative w-full bg-elevated"
      style={{ aspectRatio: "16 / 10" }}
    >
      <video
        ref={videoRef}
        src={demo.video}
        poster={demo.poster}
        muted
        loop
        playsInline
        preload="none"
        aria-label={demo.title}
        onError={() => setBroken(true)}
        className="absolute inset-0 h-full w-full object-cover object-top"
      />
      {broken && (
        <img
          src={demo.poster}
          alt={demo.title}
          loading="lazy"
          className="absolute inset-0 h-full w-full object-cover object-top"
        />
      )}
    </div>
  );
}

/** Minimal dark browser chrome around the capture. */
function BrowserFrame({ url, children }: { url: string; children: React.ReactNode }) {
  return (
    <div className="overflow-hidden rounded-xl border border-default bg-panel shadow-panel">
      <div className="flex items-center gap-3 border-b border-default bg-elevated px-4 py-2">
        <span aria-hidden className="flex gap-1.5">
          <i className="h-2 w-2 rounded-full bg-white-tint-06 border border-strong" />
          <i className="h-2 w-2 rounded-full bg-white-tint-06 border border-strong" />
          <i className="h-2 w-2 rounded-full bg-white-tint-06 border border-strong" />
        </span>
        <span className="mx-auto flex min-w-0 max-w-[60%] items-center rounded-full border border-default bg-panel px-3 py-0.5">
          <span className="truncate font-mono text-11 tracking-mono text-tertiary">
            {url}
          </span>
        </span>
        <span aria-hidden className="w-8" />
      </div>
      {children}
    </div>
  );
}

const FIRST_DEMO = DEMOS[0]!;

export function DemoShowcase() {
  const [activeId, setActiveId] = useState(FIRST_DEMO.id);
  const active = DEMOS.find((d) => d.id === activeId) ?? FIRST_DEMO;

  return (
    <section className="border-b border-default">
      <div className="mx-auto w-full max-w-5xl px-6 py-16 md:py-24">
        <p className="text-11 font-mono uppercase tracking-mono-caps text-tertiary">
          See it work
        </p>
        <h2 className="mt-4 max-w-2xl text-28 font-medium leading-display tracking-heading text-primary">
          Watch JobWarp carry one posting from found to prepped.
        </h2>

        {/* Tab rail */}
        {/* Single scrollable row on small screens so the rail never wraps
            into ragged rows; the divider always runs under every tab. */}
        <div
          role="tablist"
          aria-label="JobWarp demos"
          className="mt-12 flex gap-6 overflow-x-auto border-b border-default [scrollbar-width:none]"
        >
          {DEMOS.map((d, i) => (
            <button
              key={d.id}
              role="tab"
              aria-selected={d.id === activeId}
              onClick={() => setActiveId(d.id)}
              className={`-mb-px flex flex-none items-center gap-2 whitespace-nowrap border-b pb-3 font-mono text-11 uppercase tracking-mono-caps transition-colors ${
                d.id === activeId
                  ? "border-accent-lime text-accent-lime"
                  : "border-transparent text-tertiary hover:text-secondary"
              }`}
            >
              <span aria-hidden>{String(i + 1).padStart(2, "0")}</span>
              {d.tab}
            </button>
          ))}
        </div>

        <div className="mt-8 grid items-start gap-8 lg:grid-cols-[minmax(0,1fr)_260px] lg:gap-12">
          <BrowserFrame url={active.url}>
            {/* keep every video mounted so switching tabs doesn't re-fetch;
                only the active one is visible and playing */}
            {DEMOS.map((d) => (
              <div key={d.id} className={d.id === activeId ? "" : "hidden"}>
                <DemoVideo demo={d} active={d.id === activeId} />
              </div>
            ))}
          </BrowserFrame>
          <div className="lg:pt-2">
            <h3 className="text-15 font-medium leading-heading tracking-heading text-primary">
              {active.title}
            </h3>
            <p className="mt-2 text-13 leading-prose text-secondary">
              {active.caption}
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
